import { Dispatch, FormEventHandler, useEffect, useRef, useState } from "react";
import {
  Box,
  Fieldset,
  Heading,
  Textarea,
  Text,
  Flex,
  Collapsible,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Annotation, useAnnotations, useSelection } from "@annotorious/react";
import { LuPencil, LuInfo } from "react-icons/lu";

import { Field } from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import "@annotorious/react/annotorious-react.css";

import { useParams } from "react-router-dom";
import MobileBottomSheet from "../MobileBottomSheet";
import { IRack, ITank } from "@/types/data";
import FormActionButtons from "./ActionButtons";
import ImageAnnotationSection from "./ImageAnnotationSection";
import RackSelector from "./TankAndRackSelector";
import HealthIssuesFieldset from "./HealthIssuesFieldset";
import ObservationSubHeader from "./ObservationSubHeader";

interface IRackObservationForm {
  nurseryId: string;
  tankData: {
    tankId?: string;
    setTankId?: Dispatch<React.SetStateAction<string>>;
    tanks?: ITank[];
    tanksLoading?: boolean;
  };
  rackData: {
    rackId?: string;
    setRackId?: Dispatch<React.SetStateAction<string>>;
    racks?: IRack[];
    racksLoading?: boolean;
  };
  note: string;
  setNote: Dispatch<React.SetStateAction<string>>;
  image?: File | string;
  createdDate?: string;
  handleSubmit: FormEventHandler;
  createError?: Error | null;
  createIsPending: boolean;
  updateMode: boolean;
  status?: "created" | "processing" | "completed" | "failed";
}

function RackObservationForm({
  nurseryId,
  tankData,
  rackData,
  note,
  setNote,
  createdDate,
  image,
  handleSubmit,
  createError,
  createIsPending,
  updateMode,
  status,
}: IRackObservationForm) {
  const drawerElement = useRef<HTMLDivElement>(null);
  const annotations = useAnnotations(3);
  const { selected: selectedAnnotations } = useSelection();
  const [editMode, setEditMode] = useState<boolean>(updateMode ? false : true);
  const [savedAnnotations, setSavedAnnotations] = useState<Annotation[]>([]);
  const { observationId } = useParams<{ observationId: string }>();
  const snapPoints = [0.15, 0.5, 1];
  const [drawerHeight, setDrawerHeight] = useState<number>(snapPoints[1]);
  const openDefaultCollapsible =
    useBreakpointValue({ base: false, md: true }) || false;

  useEffect(() => {
    if (annotations && savedAnnotations.length === 0) {
      setSavedAnnotations(structuredClone(annotations));
    }
  }, [annotations, savedAnnotations.length]);

  return (
    <Box
      display={{ base: "flex", md: "grid" }}
      flexDirection="column"
      gridTemplateColumns={{
        base: "minmax(0px, 1fr)",
        md: "minmax(0px, 1fr) 20rem",
      }}
      gridTemplateRows="minmax(0px, max-content) minmax(0px, 1fr)"
      gap={0}
      as="form"
      onSubmit={handleSubmit}
      height="100%"
      minH="calc(100vh - 3.3rem)"
      maxH="calc(100vh - 3.3rem)"
      overflow="hidden"
      ref={drawerElement}
      position="relative"
    >
      <ObservationSubHeader
        backLink={`/nurseries/${nurseryId}/tanks/${tankData.tankId}/racks/${rackData.rackId}`}
        title={createdDate ? (
          `Observation #${observationId} - ${new Date(createdDate).toLocaleString([], {
            dateStyle: "short",
            timeStyle: "short",
          })}`
        ) : (
          `New Observation
          ${new Date().toLocaleString([], {
            dateStyle: "short",
            timeStyle: "short",
          })}`
        )}
      />
      <ImageAnnotationSection
        drawerHeight={drawerHeight}
        snapPoints={snapPoints}
        image={image!}
        editMode={editMode}
        updateMode={updateMode}
        annotations={annotations}
        selectedAnnotations={selectedAnnotations}
        observationDate={createdDate}
        isCompletedStatus={status === "completed"}
      />
      <MobileBottomSheet
        drawerContainerRef={drawerElement.current}
        setDrawerHeight={setDrawerHeight}
        drawerHeight={drawerHeight}
        snapPoints={snapPoints}
      >
        <Fieldset.Root>
          <Flex alignItems="center" flexWrap="wrap">
            {(!tankData.tankId || !rackData.rackId) && (
              <RackSelector tankData={tankData} rackData={rackData} />
            )}
            <Fieldset.Legend flex="1">
              {tankData.tankId
                ? rackData.rackId
                  ? `Tank ${
                    tankData.tanks?.find(
                      (tank) => tank.id === Number(tankData.tankId),
                    )?.name || tankData.tankId
                  } | Rack ${
                    rackData.racks?.find(
                      (rack) => rack.id === Number(rackData.rackId),
                    )?.name || rackData.rackId
                  }`
                  : `Tank ${
                    tankData.tanks?.find(
                      (tank) => tank.id === Number(tankData.tankId),
                    )?.name || tankData.tankId
                  }`
                : "New Observation"}
            </Fieldset.Legend>
          </Flex>
          {status !== "processing" && updateMode && (
            <Box
              p={2}
              bg="blue.50"
              border="1px solid"
              borderColor="blue.emphasized"
              rounded="sm"
              display="flex"
              flexDirection={{ base: "row", md: "column" }}
              justifyContent={{ base: "space-between", md: "initial" }}
              alignItems={{ base: "center", md: "initial" }}
              gap={2}
            >
              <Collapsible.Root defaultOpen={openDefaultCollapsible}>
                <Collapsible.Trigger asChild>
                  <Heading
                    as="h3"
                    size="xs"
                    display="flex"
                    gap="1"
                    alignItems="center"
                    color="blue.focusRing"
                    fontWeight="semibold"
                    transition="all 0.24s ease"
                    _hover={{ opacity: 0.8, cursor: "help" }}
                  >
                    Label Validation <LuInfo />
                  </Heading>
                </Collapsible.Trigger>
                <Collapsible.Content>
                  <Text fontSize="xs">
                    Health labels are detected via machine learning, and may not
                    be 100% accurate. Review individual coral labels by clicking
                    or tapping on a fragment.
                  </Text>
                </Collapsible.Content>
              </Collapsible.Root>
              <Button
                onClick={() => setEditMode(true)}
                variant="solid"
                colorPalette="blue"
                size="xs"
                disabled={editMode}
                title={
                  editMode
                    ? "Validate mode is enabled"
                    : "Enable validating annotations"
                }
              >
                <LuPencil />
                {editMode ? "Validating" : "Validate"}
              </Button>
            </Box>
          )}
          {updateMode && (
            <HealthIssuesFieldset
              annotations={annotations}
              selectedAnnotations={selectedAnnotations}
              editMode={editMode}
              status={status}
            />
          )}
        </Fieldset.Root>
        <Field label="Notes" mt="2">
          <Textarea
            disabled={!editMode}
            onChange={(e) => setNote(e.target.value)}
            value={note}
          />
        </Field>
        <FormActionButtons
          nurseryId={nurseryId}
          tankId={tankData.tankId}
          rackId={rackData.rackId}
          observationId={observationId}
          createError={createError}
          createIsPending={createIsPending}
          note={note}
          updateMode={updateMode}
          setNote={setNote}
          editMode={editMode}
          setEditMode={setEditMode}
          annotationsCount={annotations.length}
          savedAnnotations={savedAnnotations}
        />
      </MobileBottomSheet>
    </Box>
  );
}

export default RackObservationForm;
